import { analyticsReviewsService } from "@/services/analytics-reviews-service";
import type { ReviewAnalyticsTabApi } from "@/types/analytics-reviews";
import { downloadBlob } from "@/utils/downloadFile";
import {
  downloadAnalyticsExport,
  type AnalyticsExportFormat,
} from "../../utils/analyticsFileExport";
import type {
  CategoryKey,
  PreCheckoutTrendPoint,
  RatingTrendPoint,
  ReviewTableRow,
  StarDistributionSegment,
} from "./types";

export async function downloadReviewsExport(params: {
  hotelId: string;
  from: string;
  to: string;
  tab: ReviewAnalyticsTabApi;
  format: "csv" | "xlsx";
  dataset?: "over-time" | "distribution";
}): Promise<void> {
  const blob = await analyticsReviewsService.exportReviews({
    hotelId: params.hotelId,
    from: params.from,
    to: params.to,
    tab: params.tab,
    format: params.format,
    dataset: params.dataset,
  });

  const dataset = params.dataset ?? "over-time";

  downloadBlob(
    blob,
    `reviews-${dataset}-${params.tab}-${params.hotelId}-${params.from}-${params.to}.${params.format}`,
  );
}

type ReviewsWidgetExportContext = {
  hotelId: string;
  from: string;
  to: string;
  tab: ReviewAnalyticsTabApi;
  format: AnalyticsExportFormat;
};

export function downloadReviewsDistributionExport(
  params: ReviewsWidgetExportContext & {
    segments: StarDistributionSegment[];
    totalReviews: number;
  },
): void {
  downloadAnalyticsExport(
    params.format,
    [
      {
        title: "Distribution",
        headers: ["Rating", "Count", "Percent", "Total Reviews"],
        rows: params.segments.map((segment) => [
          segment.name,
          segment.value,
          params.totalReviews > 0
            ? Math.round((segment.value / params.totalReviews) * 100)
            : 0,
          params.totalReviews,
        ]),
      },
    ],
    `reviews-distribution-${params.tab}-${params.hotelId}-${params.from}-${params.to}`,
  );
}

export function downloadReviewsPreCheckoutExport(
  params: ReviewsWidgetExportContext & {
    data: PreCheckoutTrendPoint[];
  },
): void {
  downloadAnalyticsExport(
    params.format,
    [
      {
        title: "Pre-check-out reviews",
        headers: ["Day", "Pre-check-out reviews", "Post-stay reviews"],
        rows: params.data.map((point) => [
          point.day,
          point.preCheckoutReviews,
          point.postStayReviews,
        ]),
      },
    ],
    `reviews-pre-checkout-${params.tab}-${params.hotelId}-${params.from}-${params.to}`,
  );
}

export function downloadReviewsTrendExport(
  params: ReviewsWidgetExportContext & {
    data: RatingTrendPoint[];
    categoriesSupported: boolean;
    categoryKeys: CategoryKey[];
  },
): void {
  const seriesKeys =
    params.categoriesSupported && params.categoryKeys.length > 0
      ? params.categoryKeys
      : (["overallRating"] as const);

  downloadAnalyticsExport(
    params.format,
    [
      {
        title: "Overall rating over time",
        headers: ["Day", ...seriesKeys],
        rows: params.data.map((point) => [
          point.day,
          ...seriesKeys.map((key) => point[key] ?? ""),
        ]),
      },
    ],
    `reviews-overall-rating-over-time-${params.tab}-${params.hotelId}-${params.from}-${params.to}`,
  );
}

export function downloadReviewsRelatedReviewsExport(params: {
  hotelId: string;
  from: string;
  to: string;
  tab: ReviewAnalyticsTabApi;
  format: AnalyticsExportFormat;
  reviews: ReviewTableRow[];
}): void {
  downloadAnalyticsExport(
    params.format,
    [
      {
        title: "Related Reviews",
        headers: ["Guest", "Country", "Feedback Date", "Feedback", "Rating"],
        rows: params.reviews.map((review) => [
          review.guestName,
          review.countryCode,
          review.feedbackDate,
          review.feedback,
          review.rating,
        ]),
      },
    ],
    `reviews-related-reviews-${params.tab}-${params.hotelId}-${params.from}-${params.to}`,
  );
}
